"use client";

import { createId } from "@paralleldrive/cuid2";
import posthog from "posthog-js";
import { useEffect } from "react";
import { type DistConfig, useDistConfig } from "@/components/providers/dist-config-provider";
import { descopeConfig } from "@/config/descope";
import { currentEnv } from "@/config/env";
import { posthogConfig } from "@/config/posthog";
import { useRecordAllFlags } from "@/lib/hooks/flags/use-record-all-flags";
import { useOnRouteChange } from "@/lib/hooks/use-on-route-change";
import {
  type DispatchAgoraEventProps,
  dispatchAgoraEvent,
  dispatchAgoraEventName,
} from "@/lib/utils/dispatch-agora-event";
import {
  localStorageLastKnown,
  sessionAppSessionStartedTimestamp,
  sessionStorageActiveFeatureFlags,
  sessionStorageCountry,
  sessionStorageEventIndex,
  sessionStorageFeatureFlags,
  sessionStorageOrgId,
  sessionStorageOrgName,
  sessionStorageRegion,
  sessionStorageSessionId,
  sessionStorageUserEmail,
  sessionStorageUserId,
} from "@/lib/utils/storage-apis";
import { useGetEmployee } from "@/subsystems/user/employees/hooks";
import { useGetOrganization } from "@/subsystems/user/organizations/hooks";

export function RouteChangeAnalytics() {
  useOnRouteChange((_previousPath, _currentPath) => {
    dispatchAgoraEvent({
      eventType: "product.pageview",
      eventLocation: "global",
    });
  });

  return null;
}

function getMetatag(tagName: "agora:pagename" | "agora:pagetags" | "agora:build-id" | "agora:build-timestamp") {
  return (document.querySelector(`meta[name="${tagName}"]`) as HTMLMetaElement | null)?.content;
}

let queue: Payload[] = [];
let intervalId: NodeJS.Timeout | null = null;
let timeoutId: NodeJS.Timeout | null = null;

function isAuthRoute() {
  return window.location.pathname.startsWith("/a/") || window.location.pathname === "/a";
}

function isIdentityReady() {
  // If we aren't on an auth route, we are "ready" by default
  if (!isAuthRoute()) return true;

  // Otherwise, run the full check
  const flags = sessionStorageFeatureFlags.get();
  const active = sessionStorageActiveFeatureFlags.get();
  const hasFlags = flags && !Object.values(flags).some((v) => v === "unknown");
  const hasActive = Array.isArray(active);
  const hasUser = !!sessionStorageUserId.get() && !!sessionStorageUserEmail.get();
  const hasOrg = !!sessionStorageOrgId.get() && !!sessionStorageOrgName.get();
  const hasSession = !!sessionStorageSessionId.get();
  const hasPosthogSession = !!posthog?.sessionManager?.checkAndGetSessionAndWindowId()?.sessionId;

  return hasFlags && hasActive && hasUser && hasOrg && hasSession && hasPosthogSession;
}

type Payload = {
  distConfig: DistConfig;
  eventTimestamp?: number;
} & DispatchAgoraEventProps;

const agoraQueue = {
  push: (payload: Payload) => {
    queue.push(payload);

    if (!isAuthRoute()) {
      agoraQueue.flush("public_route_bypass");
      return;
    }

    if (!intervalId) {
      intervalId = setInterval(() => {
        if (isIdentityReady()) {
          agoraQueue.flush("identity_resolved");
        }
      }, 50);
    }

    if (!timeoutId) {
      timeoutId = setTimeout(() => {
        agoraQueue.flush("timeout_reached");
      }, 3000);
    }
  },

  flush: (reason: string) => {
    if (intervalId) clearInterval(intervalId);
    if (timeoutId) clearTimeout(timeoutId);
    intervalId = null;
    timeoutId = null;

    if (queue.length === 0) return;

    const userEmail = sessionStorageUserEmail.get()?.trim();

    // Grab the latest identity state
    const latestIdentity = {
      activeFeatureFlags: sessionStorageActiveFeatureFlags.get(),
      featureFlags: sessionStorageFeatureFlags.get(),
      lastKnown: localStorageLastKnown.get(),
      orgId: sessionStorageOrgId.get(),
      orgName: sessionStorageOrgName.get(),
      descopeProjectId: descopeConfig?.projectId,
      posthogSessionId: posthog?.sessionManager?.checkAndGetSessionAndWindowId()?.sessionId,
      posthogProjectKey: posthogConfig?.projectKey,
      posthogSessionReplayUrl: posthog?.get_session_replay_url({
        withTimestamp: true,
        timestampLookBack: 5,
      }),
      sessionId: sessionStorageSessionId.get(),
      userEmail,
      userId: sessionStorageUserId.get(),
      userType: userEmail?.endsWith("@agora.finance") ? "internal" : "customer",
      env: currentEnv,
    };

    queue.forEach((event: Payload) => {
      const finalizedEvent = {
        ...event,
        ...latestIdentity, // Overwrite placeholders with real identity
        eventQueueFlushReason: reason,
        eventQueueFlushTimestamp: Date.now(),
      };

      verboseLogging(finalizedEvent);
      argosCapture(finalizedEvent);
    });

    queue = [];
  },
};

function argosCapture(payload: Payload) {
  if (!posthogConfig?.argosCapture) {
    return;
  }

  return posthog.capture(payload.eventType, {
    ...payload,
    srcApp: "dashboard",
    srcLib: "argos",

    // PostHog specific: this ensures the timestamp in PH matches
    // when the event ACTUALLY happened, not when it was flushed.
    // Must be an ISO 8601 string - Date objects serialize to {} in JSON.
    timestamp: new Date(payload.eventTimestamp || Date.now()).toISOString(),
  });
}

let pageEventCounter = 0;
let lastPageLoadTimestamp = Date.now();

function recordGlobalMetadata(eventType?: string) {
  const wallTime = Date.now();

  // Handle Page-specific Reset
  if (eventType === "product.pageview") {
    lastPageLoadTimestamp = wallTime;
    pageEventCounter = 0;
  }
  pageEventCounter++;

  // Handle Session-specific Persistence
  const sessionStartedTimestamp = sessionAppSessionStartedTimestamp.get();
  const sessionStart = Number(sessionStartedTimestamp) || wallTime;
  const sessionEventIndex = sessionStorageEventIndex.increment();

  return {
    buildId: getMetatag("agora:build-id") || "not-set",
    eventTimestamp: wallTime,
    pageEventIndex: pageEventCounter,
    pageName: getMetatag("agora:pagename") || "not-set",
    pageTags: getMetatag("agora:pagetags") || "not-set",
    buildTimestamp: getMetatag("agora:build-timestamp") || "not-set",
    sessionEventIndex: sessionEventIndex,

    // Web/URL Context
    contextReferrer: document.referrer,
    contextTitle: document.title,
    contextUrlHash: window.location.hash,
    contextUrlHref: window.location.href,
    contextUrlOrigin: window.location.origin,
    contextUrlPathname: window.location.pathname,
    contextUrlSearch: window.location.search,
    contextUserAgent: navigator.userAgent,

    // Device & Hardware
    deviceCpuCores: navigator.hardwareConcurrency || "unknown",
    deviceMemory: (navigator as Navigator & { deviceMemory?: number })?.deviceMemory || "unknown",
    devicePixelRatio: window.devicePixelRatio || 1,
    deviceScreenHeight: window.screen.height,
    deviceScreenWidth: window.screen.width,
    deviceTouchPoints: navigator.maxTouchPoints || 0,
    deviceViewportHeight: window.innerHeight,
    deviceViewportWidth: window.innerWidth,

    // Geolocation (from Cloudflare via /api/sema)
    country: sessionStorageCountry.get() || "",
    region: sessionStorageRegion.get() || "",

    // Locale & Preferences
    userColorSchemePreference: window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light",
    userHasCookiesEnabled: navigator.cookieEnabled,
    userLanguage: navigator.language,
    userLanguages: navigator.languages.join(","),
    userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,

    // Performance & Timings
    timingPageOffsetMs: wallTime - lastPageLoadTimestamp,
    timingPageStartTimestamp: lastPageLoadTimestamp,
    timingSessionOffsetMs: wallTime - sessionStart,
    timingSessionStartTimestamp: sessionStart,
  };
}

export function AgoraEventListener() {
  const { distConfig } = useDistConfig() || {};

  useEffect(() => {
    const handler = (customEvent: CustomEvent<DispatchAgoraEventProps>) => {
      const detail = customEvent?.detail;

      // We pass the eventType in to determine if we need to reset the page timer
      const metadata = recordGlobalMetadata(detail?.eventType);

      agoraQueue.push({
        ...detail,
        ...metadata,
        distConfig,
      });
    };

    function handleUnload() {
      // If the tab is closing, we must flush the queue IMMEDIATELY.
      // PostHog will attempt to use sendBeacon() under the hood if we call capture now.
      agoraQueue.flush("tab_closed");
    }

    window.addEventListener(dispatchAgoraEventName, handler as EventListener);
    window.addEventListener("beforeunload", handleUnload);

    return () => {
      window.removeEventListener(dispatchAgoraEventName, handler as EventListener);
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, [distConfig]);

  return null;
}

export function RecordAppSessionId() {
  useEffect(() => {
    const appSessionId = sessionStorageSessionId.get();
    const sessionStartedTimestamp = sessionAppSessionStartedTimestamp.get();

    if (!appSessionId || !sessionStartedTimestamp) {
      sessionStorageSessionId.set(createId());
      sessionAppSessionStartedTimestamp.set();
    }
  }, []);

  return null;
}

export function RecordCountryCode() {
  useEffect(() => {
    // Only fetch if not already stored for this session
    if (sessionStorageCountry.get() !== undefined) return;

    fetch("/api/sema")
      .then((res) => res.json())
      .then((data: { country: string; region: string }) => {
        sessionStorageCountry.set(data.country);
        sessionStorageRegion.set(data.region);
      })
      .catch(() => {
        // Silently fail — country/region will be undefined in events
      });
  }, []);

  return null;
}

function verboseLogging(payload: Payload) {
  if (payload?.distConfig?.verbose === "off") {
    return;
  }

  const { eventType, eventAction } = payload;
  const hasAction = eventAction !== undefined;

  const baseLabel = hasAction ? `${eventType} | ${eventAction}` : eventType;

  console.log(...firstLogs, baseLabel, payload);
}

const BADGE_STYLE =
  "color:#000;background:#f1d3aa;padding:2px 4px;border:2px solid #b58900;border-radius:4px;font-weight:bold;";

const firstLogs = ["%c%s%s", BADGE_STYLE, ""];

export function RecordArgosData() {
  const {
    employee: { data: employeeData, isLoading: employeeIsLoading },
  } = useGetEmployee();
  const { organization: organizationData, organizationIsLoading } = useGetOrganization();

  useEffect(() => {
    const userEmail = employeeData?.email || "";
    const userId = employeeData?.authUserId || "";

    if (!employeeIsLoading) {
      sessionStorageUserEmail.set(userEmail);
      sessionStorageUserId.set(userId);
    }

    const orgId = organizationData?.authOrganizationId || "";
    const orgName = organizationData?.name || "";

    if (!organizationIsLoading) {
      sessionStorageOrgId.set(orgId);
      sessionStorageOrgName.set(orgName);
    }

    if (!employeeIsLoading && !organizationIsLoading) {
      localStorageLastKnown.set({
        orgId,
        orgName,
        userEmail,
        userId,
      });
    }
  }, [employeeData, employeeIsLoading, organizationData, organizationIsLoading]);

  useRecordAllFlags();

  return null;
}
